import React, { useEffect, useState } from 'react';

import classNames from 'classnames';

import { IconButton } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';

import { UiButton } from './ui-button';

import { ICON_NAMES, MaterialDesignIcon } from '../util/material-design-icons';

interface Props {
  canBeDismissed?: boolean;
  message?: any;
  variant?: 'error' | 'attention' | 'informational' | null;
  /** className should be used sparingly and should not override color formatting */
  className?: string;
  onMessageDismissed?: () => void;
  /**
   * Cypress selector for identifying buttons in end-to-end testing
   * sets the data-cy attribute on the button element
   * @see https://docs.cypress.io/guides/references/best-practices#Selecting-Elements
   */
  dataCy?: string;
  /**
   * Optional button action for banners
   * button text (name) and either an onClick handler or link must be provided
   * size can be optionally provided if default xs button size does not match amount of banner text
   * button variant cannot be overridden
   */
  action?: {
    name: string;
    handler?: () => void;
    link?: string;
    size?: 'xxs' | 'xs' | 'sm' | 'default' | 'lg';
  };
}

export const UiMessageBanner = ({
  canBeDismissed,
  message,
  variant,
  className,
  onMessageDismissed,
  children,
  dataCy,
  action,
}: React.PropsWithChildren<Props>) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);

  // reopen dismissed message if message content changes
  useEffect(() => {
    setIsOpen(true);
  }, [message]);

  const bannerStyle = classNames({
    'u-background-attentionMuted u-border-attention': variant === 'attention',
    'u-background-actionMuted u-border-action': variant === 'informational',
    'u-background-errorMuted u-border-error': variant === 'error',
    'u-background-neutral2 u-border-neutral6': !variant,
  });

  const handleCloseClick = () => {
    if (onMessageDismissed) {
      onMessageDismissed();
    }

    setIsOpen(false);
  };

  const actionProps = action
    ? action.link
      ? { linkTo: action.link, target: '_blank', rel: 'noopener noreferrer' }
      : { onClick: action.handler }
    : {};

  return isOpen ? (
    <div className={`${bannerStyle} ${className || ''} u-padding u-border`} data-cy={dataCy}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item xs={canBeDismissed ? 10 : 12} className="u-flex-center u-justifyContent-center">
          {children}
          {!children ? <div className="typo-body">{message}</div> : null}
          {action && (
            <UiButton
              variant={variant === 'error' ? 'primary' : 'tertiary'}
              size={action.size || 'xs'}
              className="u-margin-left-lg"
              {...actionProps}
            >
              {action.name}
            </UiButton>
          )}
        </Grid>
        {canBeDismissed ? (
          <Grid item>
            <IconButton onClick={handleCloseClick}>
              <MaterialDesignIcon iconName={ICON_NAMES.CLOSE} />
            </IconButton>
          </Grid>
        ) : null}
      </Grid>
    </div>
  ) : null;
};
